import { useEffect } from 'react'

const ICON_LABELS: Record<string, string> = {
  'arrow-left': 'Go back',
  'arrow-right': 'Go forward',
  'caret-down': 'Expand',
  'close-circle': 'Close',
  close: 'Close',
  'double-left': 'Jump back five pages',
  'double-right': 'Jump forward five pages',
  down: 'Expand',
  ellipsis: 'More options',
  left: 'Previous page',
  menu: 'Open menu',
  minus: 'Decrease',
  plus: 'Add',
  right: 'Next page',
  search: 'Search',
  setting: 'Settings',
  'zoom-in': 'Zoom in',
  'zoom-out': 'Zoom out',
}

function getTextContent(element: Element) {
  return element.textContent?.replace(/\s+/g, ' ').trim() ?? ''
}

function hasAccessibleName(element: Element) {
  if (element.hasAttribute('aria-label') || element.hasAttribute('aria-labelledby')) return true

  if (
    (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) &&
    element.labels?.length
  ) {
    return true
  }

  return Boolean(getTextContent(element))
}

function getIconLabel(element: Element) {
  if (element.classList.contains('ant-pagination-prev')) return 'Previous page'
  if (element.classList.contains('ant-pagination-next')) return 'Next page'
  if (element.classList.contains('ant-pagination-jump-prev')) return 'Jump back five pages'
  if (element.classList.contains('ant-pagination-jump-next')) return 'Jump forward five pages'
  if (element.classList.contains('ant-input-clear-icon')) return 'Clear input'

  const icon = element.querySelector<HTMLElement>('[data-icon]')
  const iconName = icon?.getAttribute('data-icon')
  if (iconName && ICON_LABELS[iconName]) return ICON_LABELS[iconName]

  return element.classList.contains('ant-dropdown-trigger') ? 'Open menu' : 'More options'
}

/**
 * Applies stable accessibility metadata to Ant Design's runtime-generated controls.
 * Keeping this at the application boundary also covers lazy routes, popovers and modals
 * without changing their interaction behaviour.
 */
export default function AccessibilityEnhancer() {
  useEffect(() => {
    const applyAccessibilityMetadata = () => {
      // Ant Design mounts popovers and modals directly under body, so include the
      // complete application document rather than only the React root.
      const root = document.body

      root.querySelectorAll<SVGElement>('svg').forEach((svg) => {
        if (
          !svg.hasAttribute('aria-hidden') &&
          !svg.hasAttribute('aria-label') &&
          !svg.hasAttribute('aria-labelledby') &&
          svg.getAttribute('role') !== 'img'
        ) {
          svg.setAttribute('aria-hidden', 'true')
        }
      })

      root
        .querySelectorAll<HTMLElement>(
          'button [role="img"], [role="button"] [role="img"], a:not([href]) [role="img"]',
        )
        .forEach((icon) => {
          icon.setAttribute('aria-hidden', 'true')
        })

      root
        .querySelectorAll<HTMLElement>('button, [role="button"], a:not([href])')
        .forEach((control) => {
          if (!hasAccessibleName(control)) {
            control.setAttribute('aria-label', getIconLabel(control))
          }

          if (control instanceof HTMLAnchorElement && !control.hasAttribute('href')) {
            control.setAttribute('role', 'button')
          }
        })

      root
        .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
          'input:not([type="hidden"]), textarea',
        )
        .forEach((input) => {
          if (!hasAccessibleName(input)) {
            input.setAttribute('aria-label', input.placeholder || 'Input')
          }
        })
    }

    applyAccessibilityMetadata()
    const observer = new MutationObserver(applyAccessibilityMetadata)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])

  return null
}
